import { COUNTRIES, countryOf } from './campaignRatePlans';
import api from '../api/axios';

/**
 * 부가 항목 기준 — Additional Fee 표에 쓰는 항목과 기준 단가
 *
 * 여태 항목 세 개가 AdditionalFeeTable.jsx 에 문자열 배열로 박혀 있었다.
 * 항목을 늘리려면 코드를 고쳐야 했고, 기준 단가가 없어서 담당자가 매번 손으로 쳤다.
 * 사람마다 다른 값이 들어가는 것을 막으려고 기준을 한 곳에 둔다.
 * (2026-07-31 대표님: 「additional fee 의 기본 값 자체를 조정해야 될 것 같애」)
 *
 * ⚠ 단가는 **KOL 에게 지급하는 현지 통화**다. 요금표의 「옵션 추가금」과 방향이 반대다.
 *    - 옵션 추가금(campaignRatePlans.opts) = 클라이언트에게 더 받는 원화
 *    - 부가 항목 단가(여기)            = KOL 에게 더 주는 현지 통화 → 마크업을 붙여 청구한다
 *    두 값을 한 표에 넣으면 어느 쪽 방향인지 알 수 없게 되므로 자리를 나눠 둔다.
 *
 * 서버가 정본이다(2026-09-30~) — GET/POST /api/client-pricing/addon-items ({ floor, items }, 저장은 MASTER).
 * 그전에는 이 브라우저(localStorage)에만 남아서, 서버 재계산은 이 값을 몰랐다(Gencode 하한이 30,000 으로 계산됨).
 * 서버가 비어 있으면 코드 기본값(DEFAULT_ADDONS)을 쓴다. 이 브라우저에 옛 값이 남아 있으면
 * 설정 화면에서 «서버에 올리기» 로 한 번 옮길 수 있다 — 자동으로 덮어쓰지 않는다.
 */
/* v2: 항목마다 마진 하한을 따로 둔다.
   한동안 하한을 하나로 합쳐 뒀는데(전 항목 30,000), 실제로는 항목마다 다르다 —
   2nd License 30,000 · Gencode 10,000 · Add to TikTok Cart 10,000
   (2026-08-01 대표님: 「라이센스 기본 blb 수익은 30,000원부터 /
     gencode, add to tiktok cart 는 개당 10,000원」).
   합쳤던 이유는 그때 값이 전부 같아서 같은 숫자만 반복되는 칸으로 보였기 때문이다. */
const KEY = 'blb.addonItems.v2';

/** 과금 단위 — Additional Fee 표의 Mo/Qty 칸이 무엇을 세는지 */
export const UNITS = [
  { k: 'month', label: '개월' },
  { k: 'qty', label: '수량' },
  { k: 'head', label: '인당' },
];
export const unitLabel = (k) => (UNITS.find((u) => u.k === k) || {}).label || k;

/* 기준 단가(KOL 지급액)는 비워서 시작한다. 실제 지급액을 모르는 채로 숫자를 넣어 두면
   담당자가 그 값을 그대로 확정해 버린다 — 빈 칸이 틀린 값보다 안전하다.
   마진 하한(우리 수익)은 대표님이 알려 주신 값이 있으므로 채워 둔다. */
const blankV = () => Object.fromEntries(COUNTRIES.map((c) => [c.k, null]));

export const DEFAULT_ADDONS = {
  /* 항목이 자기 하한을 안 정했을 때 쓰는 값. 새로 만든 항목이 여기로 떨어진다. */
  floor: 10000,
  items: [
    /* floor = 이 항목에서 **우리가 최소로 남기는 돈**(원화). 단가와 방향이 다르다:
       단가는 KOL 에게 주는 현지 통화, floor 는 클라이언트에게 받아 남기는 원화다.

       cap(마진 상한)은 **안 적어 둔다.** 안 적으면 나라·통화 표의 최대 마진(2,000,000)에 걸린다
       (2026-08-01 대표님: 「상한도 넣을 수 있으나 안 넣으면 이제 큰 상한에 걸리는 거지」).
       부가 항목 하나가 200만원 마진까지 가는 일은 거의 없어서, 필요할 때 적으면 된다. */
    { name: '2nd License', unit: 'month', floor: 30000, v: blankV() },
    { name: 'Gencode', unit: 'qty', floor: 10000, v: blankV() },
    { name: 'Add to TikTok Cart', unit: 'qty', floor: 10000, v: blankV() },
  ],
};

const clone = (v) => JSON.parse(JSON.stringify(v));
/* 서버에서 마지막으로 받은 값 — 새로고침 직후(아직 서버 응답 전) 계산도 서버 값을 쓰게 */
const SERVER_CACHE_KEY = 'blb.addonItems.server.v1';

/** 서버·저장본 어디서 온 값이든 { floor, items } 로. 쓸 수 없는 모양이면 null.
    서버가 items 배열만 저장한 경우(예전 저장 방식)도 받는다 — 그때 floor 는 코드 기본값. */
export const normalizeAddons = (v) => {
  if (Array.isArray(v)) return { floor: DEFAULT_ADDONS.floor, items: v };
  if (!v || typeof v !== 'object' || !Array.isArray(v.items)) return null;
  return { floor: v.floor != null && v.floor !== '' ? Number(v.floor) || 0 : DEFAULT_ADDONS.floor, items: v.items };
};

/* undefined = 아직 서버에 안 물어봄(또는 서버를 못 씀) · null = 서버가 비어 있음 · 객체 = 서버 값 */
let serverAddons;

const readJSON = (key) => {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; } catch (e) { return null; }
};

/** 이 브라우저에만 남아 있는 옛 기준(서버로 옮기기 전) — 없으면 null */
export const loadLocalAddons = () => normalizeAddons(readJSON(KEY));

export const loadAddons = () => {
  if (serverAddons) return clone(serverAddons);
  if (serverAddons === null) return clone(DEFAULT_ADDONS);          // 서버가 비어 있으면 코드 기본값
  // 서버에 아직 안 물어봤으면: 마지막 서버 값 → (옛 서버·오프라인이면) 이 브라우저 값 → 기본값
  const v = normalizeAddons(readJSON(SERVER_CACHE_KEY)) || loadLocalAddons();
  // 저장본이 깨졌거나 옛 모양이어도 화면은 떠야 한다
  return v ? clone(v) : clone(DEFAULT_ADDONS);
};

/**
 * 서버에서 부가 항목 기준을 받는다. 설정 화면·요금 데이터를 받는 곳이 부른다.
 * @returns {Promise<{exists:boolean, value?:object, error?:boolean}>}
 *   exists=false,error=false → 서버가 비어 있음(코드 기본값을 쓴다)
 *   error=true → 서버를 못 씀(옛 서버 404 등) — 예전처럼 이 브라우저 값을 쓴다
 */
export const hydrateAddons = async () => {
  try {
    const { data } = await api.get('/api/client-pricing/addon-items');
    const body = data && typeof data === 'object' && 'data' in data ? data.data : data;
    if (body == null) {
      serverAddons = null;
      try { localStorage.removeItem(SERVER_CACHE_KEY); } catch (e) { /* 무시 */ }
      return { exists: false, error: false };
    }
    const v = normalizeAddons(body);
    if (!v) return { exists: false, error: true };                 // 알아볼 수 없는 모양 — 덮지 않는다
    serverAddons = v;
    try { localStorage.setItem(SERVER_CACHE_KEY, JSON.stringify(v)); } catch (e) { /* 무시 */ }
    return { exists: true, value: clone(v) };
  } catch (e) {
    return { exists: false, error: true };
  }
};

/** 서버에 저장(MASTER). 성공하면 이 브라우저 계산도 바로 새 값을 쓴다 */
export const saveAddonsToServer = async (v, session) => {
  const body = normalizeAddons(v);
  if (!body) throw new Error('부가 항목 기준 모양이 올바르지 않습니다');
  /* session: 저장 단추에서 이미 받은 확인(askCriticalConfirm) — 있으면 확인 창을 또 띄우지 않는다 */
  const payload = { floor: body.floor, items: body.items };
  if (session) await api.post('/api/client-pricing/addon-items', payload, { criticalSession: session });
  else await api.post('/api/client-pricing/addon-items', payload);
  serverAddons = body;
  try { localStorage.setItem(SERVER_CACHE_KEY, JSON.stringify(body)); } catch (e) { /* 무시 */ }
  return clone(body);
};

/** 시험용 — 서버 상태를 «아직 안 물어봄» 으로 되돌린다 */
export const __resetAddonsServer = () => { serverAddons = undefined; };
/** 이 브라우저에만 저장(옛 방식). 설정 화면은 이제 saveAddonsToServer 를 쓴다 — 시험·옛 값 보존용 */
export const saveAddons = (v) => {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* 저장 실패해도 화면은 돈다 */ }
};

export const newAddonItem = (name = '새 항목') => ({ name, unit: 'qty', v: blankV() });

/** 통화 코드(THB)로 나라 키(th)를 찾는다 — 표의 KOL Rate 가 통화로만 들어온다 */
export const ctryOfCurrency = (cur) => {
  const c = COUNTRIES.find((x) => x.cur === String(cur || '').toUpperCase());
  return c ? c.k : null;
};

/**
 * 부가 항목 목록 — 항목은 **언제나 전부** 나온다.
 *
 * 한동안 캠페인 유형마다 단가를 덮거나 「이 유형에서는 안 판다」로 끌 수 있게 해 뒀는데 뺐다
 * (2026-08-01 대표님: 「미판매라는 거는 왜 있는 거야, 굳이 없어도 될 것 같은데.
 *   이 부가 항목이 유형별로 있을 필요가 있나?」).
 *   · KOL 금액에 마진을 붙여 파는 것이라 **캠페인 유형과 무관**하다 —
 *     같은 KOL 의 2nd License 가 Mega 냐 시딩이냐로 달라질 이유가 없다.
 *   · 나라별 칸이 비어 있는 것은 「아직 기준 단가를 안 정했다」이지 「안 판다」가 아니다.
 *     그래서 항목이 사라지지 않고, 자동 채움만 안 될 뿐이다.
 *
 * @returns {{name:string, unit:string, rate:(number|null), floor:number, cap:(number|null)}[]}
 */
export function resolveAddonItems(ctry, base) {
  const b = base || loadAddons();
  const items = Array.isArray(b.items) ? b.items : [];
  return items.map((it) => ({
    name: it.name,
    unit: it.unit,
    rate: (it.v || {})[ctry] == null ? null : Number(it.v[ctry]),
    floor: floorOf(it, b),
    cap: capOf(it),                              // null = 안 정함 → 통화 표의 최대 마진
  }));
}

/** 이 항목의 마진 하한 — 항목이 안 정했으면 공통 기본값 */
const floorOf = (item, base) => {
  const own = item && item.floor;
  return Number(own != null && own !== '' ? own : base.floor) || 0;
};

/** 이 항목의 마진 상한 — **안 정했으면 null**. null 이면 나라·통화 표의 최대 마진에 걸린다 */
const capOf = (item) => {
  const own = item && item.cap;
  return own != null && own !== '' ? Number(own) : null;
};

/** 통화로 바로 뽑는다 — 표 컴포넌트가 쓰는 입구. 모르는 통화면 기준 단가만 못 채운다 */
export function addonItemsFor(currency, base) {
  return resolveAddonItems(ctryOfCurrency(currency), base);
}

/**
 * 부가 항목의 마진 하한(원).
 *
 * 항목 이름을 주면 그 항목의 값, 안 주면 공통 기본값을 돌려준다 —
 * 항목마다 다르다(2nd License 30,000 vs Gencode 10,000). 이름을 못 넘기는 자리에서는
 * 기본값으로 떨어지는데, 그러면 License 가 10,000 으로 계산되니 이름을 꼭 넘겨야 한다.
 */
export const addonFloor = (base, itemName) => {
  const b = base || loadAddons();
  if (!itemName) return Number(b.floor) || 0;
  const it = (b.items || []).find((x) => x.name === itemName);
  return it ? floorOf(it, b) : Number(b.floor) || 0;
};

/**
 * 부가 항목의 마진 상한(원) — **안 정했으면 null**.
 * null 이면 부르는 쪽이 나라·통화 표의 최대 마진을 쓴다(= 사실상 상한 없음).
 */
export const addonCap = (base, itemName) => {
  if (!itemName) return null;
  const b = base || loadAddons();
  const it = (b.items || []).find((x) => x.name === itemName);
  return it ? capOf(it) : null;
};

/** 미리보기용 — 현지 단가가 원화로 얼마인지 */
export const addonKRW = (rate, ctry) => (rate == null ? null : Number(rate) * (countryOf(ctry).fx || 1));
